import React, { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../lib/api';
import { formatINR } from '../lib/gst';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, LifeBuoy, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';

const planOptions = ['free', 'starter', 'basic', 'pro', 'scale'];

export default function Admin() {
  const { t } = useLanguage();
  const [data, setData] = useState({ totals: {}, users: [] });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(true);
  const [overrides, setOverrides] = useState({});
  const [savingUser, setSavingUser] = useState(null);
  const [savingTicket, setSavingTicket] = useState(null);
  const durationOptions = useMemo(() => [
    { label: t('permanent'), value: '' },
    { label: t('sevenDays'), value: '7' },
    { label: t('fourteenDays'), value: '14' },
    { label: t('thirtyDays'), value: '30' },
    { label: t('ninetyDays'), value: '90' },
  ], [t]);

  const load = async () => {
    (async () => {
      setLoading(true);
      try {
        const { data: payload } = await apiClient.get('/admin/overview');
        setData(payload);
        const next = {};
        (payload.users || []).forEach((u) => {
          next[u.id] = { plan: u.plan || 'free', duration: '14', reason: '' };
        });
        setOverrides(next);
      } catch {
        setData({ totals: {}, users: [] });
      } finally {
        setLoading(false);
      }
    })();
  };

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const { data: payload } = await apiClient.get('/admin/help-tickets');
      setTickets(payload.tickets || []);
    } catch {
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadTickets();
  }, []);

  const updateOverride = (userId, patch) => {
    setOverrides((current) => ({ ...current, [userId]: { ...(current[userId] || {}), ...patch } }));
  };

  const applyOverride = async (userId) => {
    const next = overrides[userId] || {};
    setSavingUser(userId);
    try {
      await apiClient.put(`/admin/users/${userId}/plan-override`, {
        plan: next.plan || 'free',
        duration_days: next.duration ? Number(next.duration) : null,
        reason: next.reason || t('superadminPlanOverride'),
      });
      toast.success(t('planOverrideApplied'));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingUser(null);
    }
  };

  const clearOverride = async (userId) => {
    setSavingUser(userId);
    try {
      await apiClient.delete(`/admin/users/${userId}/plan-override`);
      toast.success(t('planOverrideCleared'));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingUser(null);
    }
  };

  const setTicketStatus = async (ticketId, status) => {
    setSavingTicket(ticketId);
    try {
      await apiClient.patch(`/admin/help-tickets/${ticketId}`, { status, resolution_note: status === 'resolved' ? t('resolved') : '' });
      toast.success(status === 'resolved' ? t('resolved') : t('open'));
      await loadTickets();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingTicket(null);
    }
  };

  const users = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return data.users || [];
    return (data.users || []).filter((u) =>
      [u.email, u.name, u.plan, u.business_name, u.gstin].some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [data.users, query]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-orange-600 text-[10px] uppercase tracking-[0.18em] font-bold mb-2">
            <ShieldCheck className="w-4 h-4" /> {t('superadmin')}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">{t('adminOverview')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('adminIntro')}</p>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('searchUsers')} className="pl-9" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4 mb-6">
        {[
          [t('users'), data.totals?.users || 0],
          [t('paidUsers'), data.totals?.paid_users || 0],
          [t('planRevenue'), formatINR(data.totals?.amount_received || 0)],
          [t('invoiceVolume'), formatINR(data.totals?.invoice_value || 0)],
          [t('invoices'), data.totals?.invoices || 0],
        ].map(([label, value]) => (
          <div key={label} className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
            <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">{label}</div>
            <div className="font-heading text-2xl font-black text-slate-900 mt-1">{value}</div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400">{t('loading')}</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1480px] text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wider text-slate-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-bold">{t('user')}</th>
                  <th className="px-4 py-3 font-bold">{t('plan')}</th>
                  <th className="px-4 py-3 font-bold">{t('subscription')}</th>
                  <th className="px-4 py-3 font-bold">{t('planControl')}</th>
                  <th className="px-4 py-3 font-bold text-right">{t('planRevenue')}</th>
                  <th className="px-4 py-3 font-bold text-right">{t('invoiceVolume')}</th>
                  <th className="px-4 py-3 font-bold text-right">{t('invoices')}</th>
                  <th className="px-4 py-3 font-bold">{t('business')}</th>
                  <th className="px-4 py-3 font-bold">{t('gstin')}</th>
                  <th className="px-4 py-3 font-bold">{t('joined')}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const draft = overrides[u.id] || { plan: u.plan || 'free', duration: '14', reason: '' };
                  const isManual = u.subscription_source === 'manual';
                  return (
                  <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50/50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{u.name || '-'}</div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                    </td>
                    <td className="px-4 py-3"><Badge variant="outline" className="uppercase">{u.plan}</Badge></td>
                    <td className="px-4 py-3 text-slate-600">
                      <div>{u.subscription_status || 'free'}</div>
                      <div className="text-[11px] text-slate-400">{u.subscription_source || 'free'}</div>
                      {u.subscription_end && <div className="text-[11px] text-slate-400">{new Date(u.subscription_end).toLocaleDateString()}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={draft.plan}
                          onChange={(e) => updateOverride(u.id, { plan: e.target.value })}
                          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-bold uppercase"
                          data-testid={`admin-plan-select-${u.id}`}
                        >
                          {planOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                        </select>
                        <select
                          value={draft.duration}
                          onChange={(e) => updateOverride(u.id, { duration: e.target.value })}
                          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-xs font-bold"
                        >
                          {durationOptions.map((d) => <option key={d.label} value={d.value}>{d.label}</option>)}
                        </select>
                        <Button size="sm" onClick={() => applyOverride(u.id)} disabled={savingUser === u.id} className="h-9 bg-slate-900 text-white hover:bg-slate-800">
                          {t('apply')}
                        </Button>
                        {isManual && (
                          <Button size="sm" variant="outline" onClick={() => clearOverride(u.id)} disabled={savingUser === u.id} className="h-9">
                            {t('clear')}
                          </Button>
                        )}
                      </div>
                      <Input
                        value={draft.reason}
                        onChange={(e) => updateOverride(u.id, { reason: e.target.value })}
                        placeholder={t('reasonNote')}
                        className="mt-2 h-8 text-xs"
                      />
                    </td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{formatINR(u.amount_received || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{formatINR(u.invoice_value || 0)}</td>
                    <td className="px-4 py-3 text-right font-mono tabular-nums">{u.invoice_count || 0}</td>
                    <td className="px-4 py-3 text-slate-700">{u.business_name || '-'}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.gstin || '-'}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.18em] text-orange-600">
              <LifeBuoy className="h-4 w-4" /> {t('supportQueue')}
            </div>
            <h2 className="font-heading text-2xl font-black text-slate-900">{t('supportQueue')}</h2>
            <p className="mt-1 text-sm text-slate-500">{t('supportQueueCopy')}</p>
          </div>
          <Badge variant="outline">{tickets.filter((ticket) => ticket.status === 'open').length} {t('open')}</Badge>
        </div>

        {ticketsLoading ? (
          <div className="text-sm text-slate-400">{t('loading')}</div>
        ) : tickets.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-6 text-sm text-slate-400">{t('noSupportTickets')}</div>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <div key={ticket.id} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={ticket.status === 'resolved' ? 'secondary' : 'outline'}>{ticket.status === 'resolved' ? t('resolved') : t('open')}</Badge>
                      <Badge variant="outline">{t('emailStatus')}: {ticket.email_status || '-'}</Badge>
                    </div>
                    <h3 className="mt-3 font-heading text-lg font-black text-slate-900">{ticket.title}</h3>
                    <div className="mt-1 text-xs text-slate-500">
                      {ticket.user_name || '-'} · {ticket.user_email || '-'} · {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '-'}
                    </div>
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-600">{ticket.body}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={ticket.status === 'resolved' ? 'outline' : 'default'}
                    onClick={() => setTicketStatus(ticket.id, ticket.status === 'resolved' ? 'open' : 'resolved')}
                    disabled={savingTicket === ticket.id}
                    className={ticket.status === 'resolved' ? '' : 'bg-slate-900 text-white hover:bg-slate-800'}
                  >
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                    {ticket.status === 'resolved' ? t('reopen') : t('resolve')}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
